// Shared across every carousel on the page, rather than one observer each.
const lazyInitObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach(({ isIntersecting, target }) => {
      if (!isIntersecting) return;

      lazyInitObserver.unobserve(target);
      target.init();
    });
  },
  { rootMargin: "50% 0px" },
);

const playbackObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach(({ isIntersecting, target }) => {
      target.inView = isIntersecting;
      target.updatePlayback();
    });
  },
  { threshold: 0.5 },
);

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

class ProductCardCarousel extends HTMLElement {
  connectedCallback() {
    const slides = this.querySelectorAll(".product-card__slide");

    this.activeSlide = slides[0];
    this.videos = this.querySelectorAll(".product-card__video");
    this.abortController = new AbortController();

    if (this.videos.length) this.bindVideos();
    if (slides.length > 1) {
      lazyInitObserver.observe(this);
      this.deferSlideImages();
    }
  }

  disconnectedCallback() {
    lazyInitObserver.unobserve(this);
    playbackObserver.unobserve(this);
    this.abortController?.abort();
    this.slideImagesController?.abort();
    this.swiper?.destroy();
    this.swiper = null;
  }

  init() {
    this.scrollbar = this.querySelector(".product-card__scrollbar");
    this.thumb = this.querySelector(".product-card__scrollbar-thumb");
    this.slideCount = this.querySelectorAll(".product-card__slide").length;
    this.transitioning = false;

    window.loadSwiper.then(({ Swiper, Navigation }) => {
      if (!this.isConnected || this.swiper) return;

      // Handlers take the instance as an argument: Swiper fires them during
      // construction, before `this.swiper` is assigned.
      this.swiper = new Swiper(this, {
        modules: [Navigation],
        slidesPerView: 1,
        spaceBetween: 2,
        loop: true,
        nested: Boolean(this.parentElement?.closest(".swiper")),
        navigation: {
          nextEl: this.querySelector("[data-carousel-next]"),
          prevEl: this.querySelector("[data-carousel-previous]"),
        },
        on: {
          setTransition: (_swiper, duration) => {
            this.setScrollbarTransition(duration);
          },
          setTranslate: (swiper) => this.updateScrollbar(swiper),
          transitionEnd: (swiper) => {
            this.setScrollbarTransition(0);
            this.updateScrollbar(swiper);
          },
          realIndexChange: (swiper) => {
            this.activeSlide = swiper.slides[swiper.activeIndex];
            this.userPaused = reducedMotion.matches;
            this.updatePlayback();
          },
        },
      });
    });
  }

  /**
   * Slides past the first render without `src`/`srcset` (see
   * snippet.product-card), and get them on the first sign the gallery is
   * about to be browsed. `loading` goes too: a lazy image clipped by the
   * carousel may otherwise wait to be scrolled into view before fetching.
   */
  deferSlideImages() {
    this.slideImagesController = new AbortController();
    const { signal } = this.slideImagesController;

    const load = () => {
      this.slideImagesController.abort();

      this.querySelectorAll("img[data-srcset]").forEach((image) => {
        image.removeAttribute("loading");
        image.srcset = image.dataset.srcset;
        image.src = image.dataset.src;
        delete image.dataset.srcset;
        delete image.dataset.src;
      });
    };

    ["pointerenter", "pointerdown", "focusin"].forEach((type) => {
      this.addEventListener(type, load, { signal });
    });
  }

  /**
   * Videos play only while their slide is active and the card is at least half
   * on screen. A pause from the button holds until the slide changes, as in the
   * main media gallery; reduced motion starts every video paused.
   */
  bindVideos() {
    const { signal } = this.abortController;

    this.videoToggle = this.querySelector("[data-video-toggle]");
    this.inView = false;
    this.userPaused = reducedMotion.matches;

    // Media events don't bubble, but they do run the capture phase.
    this.addEventListener("play", () => this.syncVideoToggle(), {
      capture: true,
      signal,
    });
    this.addEventListener("pause", () => this.syncVideoToggle(), {
      capture: true,
      signal,
    });
    this.addEventListener(
      "playing",
      ({ target }) => target.toggleAttribute("data-ready", true),
      { capture: true, signal },
    );

    this.videoToggle?.addEventListener(
      "click",
      () => {
        const video = this.activeVideo;

        if (!video) return;

        // Played directly, not through updatePlayback: a card under half on
        // screen still plays when asked to.
        this.userPaused = !video.paused;

        if (this.userPaused) {
          video.pause();
        } else {
          video.play().catch(() => this.syncVideoToggle());
        }
      },
      { signal },
    );

    playbackObserver.observe(this);
  }

  get activeVideo() {
    return this.activeSlide?.querySelector(".product-card__video") ?? null;
  }

  updatePlayback() {
    const { activeVideo } = this;

    this.videos.forEach((video) => {
      if (video === activeVideo && this.inView && !this.userPaused) {
        video.play().catch(() => this.syncVideoToggle());
      } else {
        video.pause();
      }
    });

    this.syncVideoToggle();
  }

  syncVideoToggle() {
    if (!this.videoToggle) return;

    const { activeVideo } = this;

    this.videoToggle.hidden = !activeVideo;
    this.videoToggle.toggleAttribute(
      "data-playing",
      Boolean(activeVideo && !activeVideo.paused),
    );
  }

  setScrollbarTransition(duration) {
    this.transitioning = duration > 0;

    if (this.thumb) this.thumb.style.transitionDuration = `${duration}ms`;
  }

  /**
   * Positions the scrollbar thumb by real slide index rather than Swiper's
   * `progressLoop`, which never carries the thumb to the track's end and jumps
   * at the loop seam.
   *
   * An animated slide change only reports its end translate, which the thumb
   * then transitions to. So while animating, the position steps on from the
   * last one unwrapped — past the last slide it runs on towards the slide
   * count rather than back to 0, which would carry the thumb the wrong way
   * across the track. `transitionEnd` wraps it back into range.
   */
  updateScrollbar(swiper) {
    if (!this.scrollbar || !swiper.size) return;

    const offset = -swiper.translate / swiper.size;
    const position = this.transitioning
      ? this.position + offset - this.offset
      : this.realPosition(swiper, offset);

    this.offset = offset;
    this.position = position;
    this.scrollbar.style.setProperty(
      "--product-card-scroll-position",
      position,
    );
  }

  /**
   * Loop mode reorders slides in the DOM and compensates the translate in the
   * same task, so the slide under the offset always carries the right
   * `data-swiper-slide-index`, even mid-reorder.
   *
   * @param {number} offset - Translate in slide widths.
   * @returns {number} Real slide index plus progress towards the next, in
   *   `[0, slideCount)`.
   */
  realPosition({ slides }, offset) {
    const index = Math.min(Math.max(Math.floor(offset), 0), slides.length - 1);
    const realIndex = Number(slides[index].dataset.swiperSlideIndex);

    return (realIndex + offset - index + this.slideCount) % this.slideCount;
  }
}

if (!customElements.get("product-card-carousel")) {
  customElements.define("product-card-carousel", ProductCardCarousel);
}
